import React, { useState, useEffect, useMemo, useRef } from 'react';
import { convertUrlsToMarkdownLinks } from '../utils/textUtils';
import ReactMarkdown from 'react-markdown';
import { useDateRange } from '../context/DateContext';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Tooltip,
  Checkbox,
  Button,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DateRangePicker from '../components/DateRangePicker';
import { fetchData, fetchChatConversation } from '../services/api';
import dayjs from 'dayjs';

// Estilos personalizados
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '16px',
  '&.header': {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    height: '57px',
    '&.numeric': {
      width: '55px',
      minWidth: '55px',
      maxWidth: '55px',
      padding: '4px 8px',
      textAlign: 'center',
      '& .MuiTableSortLabel-root': {
        display: 'inline-flex',
        '& .MuiTableSortLabel-icon': {
          marginLeft: '0px',
          position: 'static'
        }
      }
    },
    '&.fecha': {
      width: '120px',
      minWidth: '120px',
      maxWidth: '120px',
      paddingRight: '24px',
    },
    '&.datos-personales': {
      width: '200px',
      minWidth: '200px',
      maxWidth: '200px',
    }
  },
  '&.numeric': {
    width: '55px',
    minWidth: '55px',
    maxWidth: '55px',
    padding: '4px 8px',
    textAlign: 'center',
  },
  '&.fecha': {
    paddingRight: '24px',
  },
  '&.consulta': {
    padding: '12px 16px',
    '& .consulta-text': {
      color: theme.palette.text.primary,
      marginBottom: '4px',
      display: 'block',
      wordBreak: 'break-word',
      fontSize: '0.95em',
      minWidth: '150px', // Establece un ancho máximo
      maxWidth: '400px', // Establece un ancho máximo
      whiteSpace: 'wrap', // Evita que el texto se extienda a múltiples líneas
      overflow: 'hidden', // Oculta el contenido que excede el ancho máximo
      textOverflow: 'ellipsis', // Agrega puntos suspensivos (...) para el contenido recortado
    },
    '& .respuesta-text': {
      color: theme.palette.text.secondary,
      paddingLeft: '16px',
      display: 'block',
      fontSize: '0.85em',
      wordBreak: 'break-word',
      minWidth: '150px', // Establece un ancho máximo
      maxWidth: '400px', // Establece un ancho máximo
      whiteSpace: 'wrap', // Evita que el texto se extienda a múltiples líneas
      overflow: 'hidden', // Oculta el contenido que excede el ancho máximo
      textOverflow: 'ellipsis', // Agrega puntos suspensivos (...) para el contenido recortado
    }
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.background.paper,
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.96)' : 'rgba(255, 255, 255, 0.96)',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 24px 80px rgba(0,0,0,0.42)'
      : '0 24px 80px rgba(15,23,42,0.14)',
    backdropFilter: 'blur(22px) saturate(160%)',
  },
}));

const StyledMetricBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : theme.palette.background.default,
  padding: '14px',
  borderRadius: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 24px rgba(0,0,0,0.16)'
    : '0 8px 24px rgba(15,23,42,0.05)',
  '& .label': {
    color: theme.palette.text.secondary,
    fontWeight: 800,
    fontSize: '0.72rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '8px',
    height: '16px',
    textAlign: 'center'
  },
  '& .value': {
    color: theme.palette.text.primary,
    fontSize: '1.35rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    textAlign: 'center',
    maxWidth: '100%',
    padding: '0 4px'
  },
  '& .personalDataValue': {
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    maxWidth: '100%',
    padding: '0 4px',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: 1.2,
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical'
  },
  '& .date': {
    color: theme.palette.text.primary,
    fontSize: '0.82rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    width: '100%'
  },
  '& .time': {
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    marginTop: '1px',
    opacity: 0.8,
    textAlign: 'center',
    width: '100%'
  }
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  fontSize: '0.9rem',
  marginTop: '16px',
  marginBottom: '8px',
  marginLeft: '8px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: '-8px',
    right: 0,
    height: '1px',
    backgroundColor: theme.palette.divider
  }
}));

const DialogSectionLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  fontSize: '0.9rem',
  marginTop: '24px',
  marginBottom: '16px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: theme.palette.divider,
    marginBottom: '8px'
  }
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.default
    : theme.palette.background.paper,
  padding: '16px',
  borderRadius: '8px',
  marginTop: '24px',
  border: `1px solid ${theme.palette.divider}`,
  minHeight: '100px',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.paper,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  maxWidth: '70%',
  marginBottom: '12px',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : theme.palette.background.paper,
  padding: '12px 16px',
  borderRadius: '12px',
  borderTopRightRadius: isUser ? '4px' : '12px',
  borderTopLeftRadius: isUser ? '12px' : '4px',
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  border: isUser
    ? 'none'
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.10)'}`,
  boxShadow: isUser
    ? '0 1px 2px rgba(0,0,0,0.2)'
    : theme.palette.mode === 'dark'
      ? '0 6px 18px rgba(0,0,0,0.18)'
      : '0 2px 8px rgba(15,23,42,0.06)',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    [isUser ? 'right' : 'left']: '-8px',
    borderTop: `8px solid ${isUser
      ? theme.palette.primary.main
      : theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : theme.palette.background.paper}`,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
  },
}));

const formatChatText = (text) => {
  if (!text) return [];

  const messages = [];
  let currentSpeaker = null;
  let currentLines = [];

  // Split by lines and process each line
  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this is a new speaker line
    if (line.startsWith('Usuario : ')) {
      // If we have a previous message, add it to messages
      if (currentSpeaker) {
        messages.push({
          isUser: currentSpeaker === 'Usuario',
          speaker: currentSpeaker,
          message: currentLines.join('\n')
        });
      }

      // Start a new user message
      currentSpeaker = 'Usuario';
      currentLines = [line.substring('Usuario : '.length).trim()];
    }
    else if (line.startsWith('4LogicONE : ') || line.startsWith('AI4Assistant : ') || line.startsWith('Asistente : ')) {
      // If we have a previous message, add it to messages
      if (currentSpeaker) {
        messages.push({
          isUser: currentSpeaker === 'Usuario',
          speaker: currentSpeaker,
          message: currentLines.join('\n')
        });
      }

      // Start a new assistant message
      currentSpeaker = (line.startsWith('4LogicONE : ') || line.startsWith('AI4Assistant : ')) ? 'AI4Assistant' : 'Asistente';
      currentLines = [line.substring(line.indexOf(' : ') + 3).trim()];
    }
    else if (currentSpeaker) {
      // This is a continuation of the current message
      // We don't trim the line to preserve empty lines for markdown formatting
      currentLines.push(line);
    }
  }

  // Add the last message if there is one
  if (currentSpeaker) {
    messages.push({
      isUser: currentSpeaker === 'Usuario',
      speaker: currentSpeaker,
      message: currentLines.join('\n')
    });
  }

  return messages;
};

const formatTime = (fecha, hora) => {
  return dayjs(fecha + hora, 'YYYYMMDDHHmmss').format('HH:mm:ss');
};

const formatDate = (fecha) => {
  return dayjs(fecha, 'YYYYMMDD').format('DD/MM/YYYY');
};

const formatDateTimeForBox = (fecha, hora) => {
  return {
    date: dayjs(fecha, 'YYYYMMDD').format('DD/MM/YYYY'),
    time: dayjs(hora, 'HHmmss').format('HH:mm:ss')
  };
};

const hasPersonalData = (chatDetail) => {
  return chatDetail.nombre || chatDetail.telefono || chatDetail.email ||
    chatDetail.direccion || chatDetail.referencia || chatDetail.referencias || chatDetail.empresa;
};

const getPersonalDataBoxes = (chatDetail) => {
  const boxes = [];
  if (chatDetail.nombre) boxes.push({ label: 'Nombre', value: chatDetail.nombre });
  if (chatDetail.telefono) boxes.push({ label: 'Teléfono', value: chatDetail.telefono });
  if (chatDetail.email) boxes.push({ label: 'Email', value: chatDetail.email });
  if (chatDetail.direccion) boxes.push({ label: 'Dirección', value: chatDetail.direccion });
  if (chatDetail.empresa) boxes.push({ label: 'Empresa', value: chatDetail.empresa });
  if (chatDetail.referencia) boxes.push({ label: 'Referencia', value: chatDetail.referencia });
  if (chatDetail.referencias) boxes.push({ label: 'Referencias', value: chatDetail.referencias });
  return boxes;
};

const Conversations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [chatDetail, setChatDetail] = useState(null);
  const { startDate, endDate, setStartDate, setEndDate } = useDateRange();
  const { selectedChatbot } = useSelectedChatbot();
  const [orderBy, setOrderBy] = useState('fecha');
  const [order, setOrder] = useState('desc');
  const [showOnlyWithPersonalData, setShowOnlyWithPersonalData] = useState(false);

  // Estado inicial de los filtros
  const [urlFilters, setUrlFilters] = useState({
    valoration: null,
    urgency: null,
    resolution: null,
    relevance: null,
    keyword: null,
    intent: null,
    pattern: null,
    contact: null
  });

  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = false; // Reseteamos el estado de arrastre
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (e.buttons !== 1) return; // Asegúrate de que el botón del mouse esté presionado
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1; // Sensibilidad
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
    isDragging.current = true; // Detecta que el usuario está arrastrando
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      isDragging.current = false; // Resetea el estado después de soltar
    }, 0);
  };

  const handleClick = (e) => {
    if (isDragging.current) {
      e.preventDefault(); // Cancela el clic si fue un arrastre
      isDragging.current = false;
    }
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    startX.current = e.touches[0].pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Efecto para cargar los filtros de la URL
  useEffect(() => {
    const newFilters = {
      valoration: searchParams.get('valoration') !== null ? parseInt(searchParams.get('valoration')) : null,
      urgency: searchParams.get('urgency') !== null ? parseInt(searchParams.get('urgency')) : null,
      resolution: searchParams.get('resolution') !== null ? parseInt(searchParams.get('resolution')) : null,
      relevance: searchParams.get('relevance') !== null ? parseInt(searchParams.get('relevance')) : null,
      keyword: searchParams.get('keyword'),
      intent: searchParams.get('intent'),
      pattern: searchParams.get('pattern'),
      contact: searchParams.get('contact')
    };
    setUrlFilters(newFilters);
  }, [searchParams]);

  // Función para obtener el nombre del filtro
  const getFilterName = (filterType, value) => {
    switch (filterType) {
      case 'valoration':
        return `Valoración: ${value}`;
      case 'urgency':
        return `Urgencia: ${value}`;
      case 'resolution':
        return `Resolución: ${value}`;
      case 'relevance':
        return `Relevancia: ${value}`;
      case 'keyword':
        return `Palabra clave: ${value}`;
      case 'intent':
        return `Intención: ${value}`;
      case 'pattern':
        return `Patrón: ${value}`;
      case 'contact':
        const contactTypeNames = {
          'email': 'Email',
          'phone': 'Teléfono',
          'name': 'Nombre',
          'company': 'Empresa',
          'references': 'Referencias'
        };
        return `Contacto: ${contactTypeNames[value] || value}`;
      default:
        return '';
    }
  };

  // Función para obtener el color del filtro
  const getFilterColor = (filterType) => {
    switch (filterType) {
      case 'valoration':
        return '#9c27b0';
      case 'urgency':
        return '#2e7d32';
      case 'resolution':
        return '#1976d2';
      case 'relevance':
        return '#ed6c02';
      case 'keyword':
        return '#4fc3f7';
      case 'intent':
        return '#7986cb';
      case 'pattern':
        return '#ff7043';
      case 'contact':
        return '#26a69a'; // Color turquesa para contacto
      default:
        return theme.palette.text.secondary;
    }
  };

  const filteredAndSortedChats = useMemo(() => {
    let filtered = [...chats];

    if (showOnlyWithPersonalData) {
      filtered = filtered.filter(hasPersonalData);
    }

    // Aplicar filtros de URL solo si tienen valores válidos
    if (urlFilters.valoration !== null) {
      filtered = filtered.filter(chat => {
        const valoracion = parseInt(chat.valoracion);
        return !isNaN(valoracion) && valoracion === urlFilters.valoration;
      });
    }
    if (urlFilters.urgency !== null) {
      filtered = filtered.filter(chat => {
        const urgencia = parseInt(chat.urgencia);
        return !isNaN(urgencia) && urgencia === urlFilters.urgency;
      });
    }
    if (urlFilters.resolution !== null) {
      filtered = filtered.filter(chat => {
        const resuelto = parseInt(chat.resuelto);
        return !isNaN(resuelto) && resuelto === urlFilters.resolution;
      });
    }
    if (urlFilters.relevance !== null) {
      filtered = filtered.filter(chat => {
        const relevancia = parseInt(chat.relevancia);
        return !isNaN(relevancia) && relevancia === urlFilters.relevance;
      });
    }
    if (urlFilters.keyword) {
      filtered = filtered.filter(chat => {
        const keyword = urlFilters.keyword.toLowerCase().trim();
        const tagList = (chat.tags || '').toLowerCase().split(',').map(tag => tag.trim());
        return tagList.includes(keyword);
      });
    }
    if (urlFilters.intent) {
      filtered = filtered.filter(chat => {
        return chat.intencion === urlFilters.intent;
      });
    }
    if (urlFilters.pattern) {
      filtered = filtered.filter(chat => {
        return chat.patron === urlFilters.pattern;
      });
    }
    if (urlFilters.contact) {
      filtered = filtered.filter(chat => {
        const contactTypeToField = {
          'email': 'email',
          'phone': 'telefono',
          'name': 'nombre',
          'company': 'empresa',
          'references': 'referencias'
        };
        const field = contactTypeToField[urlFilters.contact];
        return field && chat[field] && chat[field].trim() !== '';
      });
    }

    return filtered.sort((a, b) => {
      if (orderBy === 'fecha') {
        const dateA = dayjs(a.fecha + ' ' + a.hora, 'YYYYMMDD HHmmss');
        const dateB = dayjs(b.fecha + ' ' + b.hora, 'YYYYMMDD HHmmss');
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      let valueA = a[orderBy] || 0;
      let valueB = b[orderBy] || 0;

      // Si es un valor numérico, convertirlo
      if (typeof valueA === 'string') valueA = parseInt(valueA) || 0;
      if (typeof valueB === 'string') valueB = parseInt(valueB) || 0;

      return order === 'asc' ? valueA - valueB : valueB - valueA;
    });
  }, [chats, order, orderBy, showOnlyWithPersonalData, urlFilters]);

  const getColorForValue = (value) => {
    if (value <= 3) return '#ff5252';  // Rojo para valores bajos
    if (value <= 6) return '#ffd740';  // Amarillo para valores medios
    return '#69f0ae';  // Verde para valores altos
  };

  const getColorForValueUrg = (value) => {
    if (value <= 3) return '#69f0ae';  // Verde para urgencia baja
    if (value <= 6) return '#ffd740';  // Amarillo para urgencia media
    return '#ff5252';  // Rojo para urgencia alta
  };

  useEffect(() => {
    const fetchChats = async () => {
      if (!selectedChatbot) {
        setError('No hay asistente seleccionado');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchData(
          1,
          selectedChatbot,
          startDate.format('YYYYMMDD'),
          endDate.format('YYYYMMDD')
        );
        setChats(response || []);
        setError(null);
      } catch (err) {
        console.error('Error al cargar las conversaciones:', err);
        setError('Error al cargar las conversaciones. Por favor, inténtelo de nuevo más tarde.');
        setChats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [startDate, endDate, selectedChatbot]);

  useEffect(() => {
    const handleChatbotChange = () => {
      setSelectedChat(null);
      setChatDetail(null);
      setOpenDialog(false);
      setChats([]);
      const fetchChats = async () => {
        setLoading(true);
        setError(null);
        try {
          if (!selectedChatbot) {
            setError('No hay asistente seleccionado');
            return;
          }

          const response = await fetchData(
            1,
            selectedChatbot,
            startDate.format('YYYYMMDD'),
            endDate.format('YYYYMMDD')
          );
          setChats(response || []);
          setError(null);
        } catch (err) {
          console.error('Error al cargar las conversaciones:', err);
          setError('Error al cargar las conversaciones. Por favor, inténtelo de nuevo más tarde.');
          setChats([]);
        } finally {
          setLoading(false);
        }
      };
      fetchChats();
    };

    window.addEventListener('chatbotChanged', handleChatbotChange);
    return () => {
      window.removeEventListener('chatbotChanged', handleChatbotChange);
    };
  }, [startDate, endDate, selectedChatbot]);

  const handleChatClick = async (chat) => {
    if (!selectedChatbot) {
      setError('No hay asistente seleccionado');
      return;
    }

    try {
      setSelectedChat(chat);
      setOpenDialog(true);
      const detail = await fetchChatConversation(
        1,
        selectedChatbot,
        chat.fecha,
        chat.fecha,
        chat.chatid
      );
      setChatDetail(detail?.[0] || null);
    } catch (err) {
      console.error('Error al cargar el detalle de la conversación:', err);
      setError('Error al cargar el detalle de la conversación');
    }
  };

  const formatDateTime = (fecha, hora) => {
    const formattedDate = dayjs(fecha + hora, 'YYYYMMDDHHmmss').format('DD/MM/YYYY HH:mm:ss');
    return formattedDate;
  };

  const handleExportCSV = () => {
    if (!selectedChatbot) {
      setError('No hay asistente seleccionado');
      return;
    }

    // Crear el contenido del CSV
    const headers = [
      'Asistente',
      'Fecha',
      'Hora',
      'Usuario',
      'ChatID',
      'Version',
      'Nombre',
      'Email',
      'Telefono',
      'Direccion',
      'Empresa',
      'Otros',
      'Referencias',
      'Consulta',
      'Respuesta',
      'Valoracion',
      'Urgencia',
      'Resuelto',
      'Relevancia'
    ].join(';');

    const csvContent = filteredAndSortedChats.map(chat => {
      // Asegurarse de que los campos con punto y coma o saltos de línea estén correctamente formateados
      const formatField = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        if (stringField.includes(';') || stringField.includes('\n') || stringField.includes('"')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      return [
        formatField(selectedChatbot),
        formatField(formatDate(chat.fecha)),
        formatField(formatTime(chat.fecha, chat.hora)),
        formatField(chat.usuario),
        formatField(chat.chatid),
        formatField(chat.version),
        formatField(chat.nombre),
        formatField(chat.email),
        formatField(chat.telefono),
        formatField(chat.direccion),
        formatField(chat.empresa),
        formatField(chat.otros),
        formatField(chat.referencias),
        formatField(chat.consulta),
        formatField(chat.respuesta),
        formatField(chat.valoracion),
        formatField(chat.urgencia),
        formatField(chat.resuelto),
        formatField(chat.relevancia)
      ].join(';');
    });

    // Combinar headers y contenido
    const csv = [headers, ...csvContent].join('\n');

    // Crear el blob y descargar
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `conversaciones_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleCopyChatId = () => {
    navigator.clipboard.writeText(selectedChat.chatid);
    setSnackbar({
      open: true,
      message: 'ID copiado al portapapeles',
      severity: 'success'
    });
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 0, sm: 0 },
        backgroundColor: 'transparent',
        minHeight: '100vh',
        px: { xs: 0, sm: 2, md: 3 },
        overflow: 'hidden',
        maxWidth: '100vw',
      }}
    >
      <Box sx={{
        mx: { xs: 1, sm: 0 },
        mb: { xs: 3, sm: 4 },
      }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 2, sm: 2.5 },
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{
              color: theme.palette.text.primary,
              marginBottom: { xs: 4, sm: 0.5 },
              fontWeight: 800,
              letterSpacing: '-0.03em',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            }}>
              Conversaciones
            </Typography>
            <Typography variant="body1" sx={{
              color: theme.palette.text.secondary,
              fontSize: '1rem',
              fontWeight: 500,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            }}>
              Historial de conversaciones de AI4Assistant
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            alignItems: { xs: 'stretch', sm: 'center' },
            width: { xs: '100%', sm: 'auto' },
            mt: { xs: 1, sm: 0 },
            flexWrap: 'wrap'
          }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon sx={{ color: theme.palette.primary.contrastText }} />}
              onClick={handleExportCSV}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
                height: '40px',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                width: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                  '& svg': {
                    fontSize: '1.2rem'
                  }
                }
              }}
            >
              Exportación CSV
            </Button>
          </Box>
        </Paper>

        {Object.entries(urlFilters).some(([_, value]) => value !== null) && (
          <Paper
            sx={{
              p: 2,
              borderRadius: '8px',
              mb: 0,
              mt: 3,
              minHeight: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
              '& > *': {
                display: 'flex',
                alignItems: 'center'
              }
            }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  color: theme.palette.text.secondary,
                  mr: 1,
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  lineHeight: '32px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Filtros activos:
              </Typography>
              {Object.entries(urlFilters).map(([key, value]) => {
                if (value === null) return null;
                return (
                  <Chip
                    key={key}
                    label={getFilterName(key, value)}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      border: `0.5px solid ${getFilterColor(key)}`,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      color: getFilterColor(key),
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiChip-label': {
                        lineHeight: 'normal',
                        height: '100%',
                        padding: '0 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: getFilterColor(key),
                        '&:hover': {
                          color: theme.palette.text.primary
                        }
                      }
                    }}
                  />
                );
              })}
              <Button
                variant="contained"
                startIcon={<ClearIcon sx={{ color: theme.palette.primary.contrastText }} />}
                size="small"
                onClick={() => navigate('/conversations')}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  ml: 'auto',
                  color: theme.palette.primary.contrastText,
                  border: `1px solid ${theme.palette.primary.main}`,
                  height: '32px',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '32px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    borderColor: theme.palette.primary.dark,
                  },
                }}
              >
                Limpiar filtros
              </Button>
            </Box>
          </Paper>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : filteredAndSortedChats.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 3
          }}>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              No hay registros de conversaciones para {
                Object.values(urlFilters).some(value => value !== null)
                  ? 'los filtros aplicados'
                  : 'el período seleccionado'
              }.
            </Typography>
            {Object.values(urlFilters).some(value => value !== null) && (
              <Button
                variant="outlined"
                onClick={() => navigate('/conversations')}
                sx={{
                  color: theme.palette.text.secondary,
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{
            mx: { xs: 1, sm: 0 },
            overflow: 'hidden',
            width: 'auto',
            mt: { xs: 3, sm: 3 },
          }}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Box
                ref={scrollRef}
                sx={{
                  overflowX: 'auto',
                  cursor: isDragging.current ? 'grabbing' : 'grab', // Cambia el cursor
                  width: '100%',
                  WebkitOverflowScrolling: 'touch',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  '-webkit-overflow-scrolling': 'touch',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <Table sx={{
                  minWidth: { xs: 'auto', sm: 650 },
                  backgroundColor: theme.palette.background.paper,
                  '& .MuiTableCell-root': {
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    p: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.813rem', sm: '0.875rem' }
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell className="header fecha">
                        <TableSortLabel
                          active={orderBy === 'fecha'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('fecha')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: `${theme.palette.primary.main} !important`,
                            },
                          }}
                        >
                          Fecha
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell className="header">
                        Consulta
                      </StyledTableCell>
                      <StyledTableCell className="header datos-personales">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Datos personales
                          <Tooltip title="Mostrar solo conversaciones con datos personales">
                            <Checkbox
                              size="small"
                              checked={showOnlyWithPersonalData}
                              onChange={(e) => setShowOnlyWithPersonalData(e.target.checked)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                                padding: 0,
                                marginLeft: '4px'
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell className="header numeric">
                        <TableSortLabel
                          active={orderBy === 'nummsj'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('nummsj')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiTableSortLabel-icon': {
                              marginLeft: '4px',
                              position: 'static',
                              opacity: 0,
                              transition: 'opacity 0.2s'
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                            '&.Mui-active': {
                              color: theme.palette.text.primary,
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                        >
                          <Tooltip title="Número de mensajes en la conversación">
                            <span>Msjs</span>
                          </Tooltip>
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell className="header">
                        <TableSortLabel
                          active={orderBy === 'usuario'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('usuario')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: `${theme.palette.primary.main} !important`,
                            },
                          }}
                        >
                          Usuario
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell
                        className="header numeric"
                        sx={{
                          color: `${getColorForValue(urlFilters.relevance)} !important`,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                        <TableSortLabel
                          active={orderBy === 'relevancia'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('relevancia')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiTableSortLabel-icon': {
                              marginLeft: '4px',
                              position: 'static',
                              opacity: 0,
                              transition: 'opacity 0.2s'
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                            '&.Mui-active': {
                              color: theme.palette.text.primary,
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                        >
                          <Tooltip title="Relevancia: Es un valor numérico subjetivo del 0 al 10 indicando si la conversación es relevante para la empresa">
                            <span>Rel</span>
                          </Tooltip>
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell
                        className="header numeric"
                        sx={{
                          color: `${getColorForValueUrg(urlFilters.urgency)} !important`,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                        <TableSortLabel
                          active={orderBy === 'urgencia'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('urgencia')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiTableSortLabel-icon': {
                              marginLeft: '4px',
                              position: 'static',
                              opacity: 0,
                              transition: 'opacity 0.2s'
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                            '&.Mui-active': {
                              color: theme.palette.text.primary,
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                        >
                          <Tooltip title="Urgencia: Es un valor numérico subjetivo del 0 al 10 indicando la urgencia de la conversación">
                            <span>Urg</span>
                          </Tooltip>
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell
                        className="header numeric"
                        sx={{
                          color: `${getColorForValue(urlFilters.valoracion)} !important`,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                        <TableSortLabel
                          active={orderBy === 'valoracion'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('valoracion')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiTableSortLabel-icon': {
                              marginLeft: '4px',
                              position: 'static',
                              opacity: 0,
                              transition: 'opacity 0.2s'
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                            '&.Mui-active': {
                              color: theme.palette.text.primary,
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                        >
                          <Tooltip title="Valoración: Es un valor numérico subjetivo del 0 al 10 indicando si la conversación es valiosa comercialmente">
                            <span>Val</span>
                          </Tooltip>
                        </TableSortLabel>
                      </StyledTableCell>
                      <StyledTableCell
                        className="header numeric"
                        sx={{
                          color: `${getColorForValue(urlFilters.resuelto)} !important`,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                        <TableSortLabel
                          active={orderBy === 'resuelto'}
                          direction={order === 'asc' ? 'asc' : 'desc'}
                          onClick={() => handleRequestSort('resuelto')}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& .MuiTableSortLabel-icon': {
                              marginLeft: '0px',
                              position: 'static',
                              opacity: 0,
                              transition: 'opacity 0.2s'
                            },
                            '&:hover .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                            '&.Mui-active': {
                              color: theme.palette.text.primary,
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                        >
                          <Tooltip title="Resolución: Es un valor numérico subjetivo del 0 al 10 indicando si se ha resuelto la consulta">
                            <span>Res</span>
                          </Tooltip>
                        </TableSortLabel>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(filteredAndSortedChats || []).map((chat) => (
                      <StyledTableRow
                        key={chat.chatid}
                        onClick={() => handleChatClick(chat)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                            <Typography sx={{ color: theme.palette.text.primary, fontSize: '0.875rem' }}>
                              {formatDate(chat.fecha)}
                            </Typography>
                            <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.813rem', mt: 0.5 }}>
                              {formatTime(chat.fecha, chat.hora)}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell className="consulta">
                          <span className="consulta-text">{chat.consulta}</span>
                          <span className="respuesta-text">{chat.respuesta}</span>
                        </StyledTableCell>
                        <StyledTableCell>
                          {[
                            chat.nombre,
                            chat.telefono,
                            chat.email,
                            chat.direccion,
                            chat.empresa,
                            chat.referencias
                          ].filter(Boolean).join(', ') || ''}
                        </StyledTableCell>
                        <StyledTableCell className="numeric" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {chat.nummsj}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: theme.palette.text.primary }}>
                          {chat.usuario}
                        </StyledTableCell>
                        <StyledTableCell
                          className="numeric"
                          sx={{
                            color: `${getColorForValue(chat.relevancia)} !important`,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}>
                          {chat.relevancia}
                        </StyledTableCell>
                        <StyledTableCell
                          className="numeric"
                          sx={{
                            color: `${getColorForValueUrg(chat.urgencia)} !important`,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}>
                          {chat.urgencia}
                        </StyledTableCell>
                        <StyledTableCell
                          className="numeric"
                          sx={{
                            color: `${getColorForValue(chat.valoracion)} !important`,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}>
                          {chat.valoracion}
                        </StyledTableCell>
                        <StyledTableCell
                          className="numeric"
                          sx={{
                            color: `${getColorForValue(chat.resuelto)} !important`,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}>
                          {chat.resuelto}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Box>
        )}

        <StyledDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ m: 0, p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              pr: 4 // Espacio para el botón de cerrar
            }}>
              <Typography variant="h5" component="div" sx={{ color: theme.palette.text.primary, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Detalle de la Conversación
              </Typography>
              <Chip
                label={chatDetail?.chatid}
                size="small"
                deleteIcon={<ContentCopyIcon sx={{ fontSize: '0.75rem' }} />}
                onDelete={handleCopyChatId}
                sx={{
                  height: '28px',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '999px',
                  '& .MuiChip-label': {
                    color: theme.palette.text.secondary,
                    opacity: 0.85,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    px: 1.25
                  },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                    '&:hover': {
                      color: theme.palette.text.primary,
                      opacity: 1
                    }
                  }
                }}
              />
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setOpenDialog(false)}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
                '&:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, backgroundColor: 'transparent' }}>
            {chatDetail && (
              <>
                <DialogSectionLabel sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Información
                </DialogSectionLabel>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StyledMetricBox sx={{ minHeight: '90px' }}>
                      <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Fecha</Typography>
                      <Typography className="date" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                        {formatDateTimeForBox(chatDetail.fecha, chatDetail.hora).date}
                      </Typography>
                      <Typography className="time" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                        {formatDateTimeForBox(chatDetail.fecha, chatDetail.hora).time}
                      </Typography>
                    </StyledMetricBox>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StyledMetricBox sx={{ minHeight: '90px' }}>
                      <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Relevancia</Typography>
                      <Typography
                        className="value"
                        sx={{ color: getColorForValue(chatDetail.relevancia), fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      >
                        {chatDetail.relevancia}
                      </Typography>
                    </StyledMetricBox>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StyledMetricBox sx={{ minHeight: '90px' }}>
                      <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Urgencia</Typography>
                      <Typography
                        className="value"
                        sx={{ color: getColorForValueUrg(chatDetail.urgencia), fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      >
                        {chatDetail.urgencia}
                      </Typography>
                    </StyledMetricBox>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StyledMetricBox sx={{ minHeight: '90px' }}>
                      <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Valoración</Typography>
                      <Typography
                        className="value"
                        sx={{ color: getColorForValue(chatDetail.valoracion), fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      >
                        {chatDetail.valoracion}
                      </Typography>
                    </StyledMetricBox>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StyledMetricBox sx={{ minHeight: '90px' }}>
                      <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Resolución</Typography>
                      <Typography
                        className="value"
                        sx={{ color: getColorForValue(chatDetail.resuelto), fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      >
                        {chatDetail.resuelto}
                      </Typography>
                    </StyledMetricBox>
                  </Grid>
                </Grid>

                {hasPersonalData(chatDetail) && (
                  <>
                    <DialogSectionLabel sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      Datos Personales
                    </DialogSectionLabel>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {getPersonalDataBoxes(chatDetail).map((box, index) => (
                        <Grid
                          item
                          key={box.label}
                          xs={12}
                          sm={6}
                          md={2.4}
                        >
                          <StyledMetricBox sx={{ minHeight: '90px' }}>
                            <Typography className="label" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>{box.label}</Typography>
                            <Typography className="personalDataValue" sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                              {box.value}
                            </Typography>
                          </StyledMetricBox>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                <ChatContainer sx={{ borderRadius: '20px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.02)' : 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '50px',
                    gap: 0.5,
                  }}>
                    {formatChatText(chatDetail.texto).map((msg, index) => (
                      <MessageBubble key={index} isUser={msg.isUser}>
                        <Typography variant="caption" sx={{
                          color: msg.isUser ? theme.palette.primary.contrastText : theme.palette.primary.main,
                          display: 'block',
                          marginBottom: '6px',
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                        }}>
                          {msg.speaker}
                        </Typography>
                        <Box sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: 'inherit' }}>
                          <ReactMarkdown components={{
                            p: ({ node, ...props }) => <p style={{ marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,
                            a: ({ node, ...props }) => <a style={{ color: theme.palette.primary.main, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />
                          }}>{convertUrlsToMarkdownLinks(msg.message)}</ReactMarkdown>
                        </Box>
                      </MessageBubble>
                    ))}
                  </Box>
                </ChatContainer>
              </>
            )}
          </DialogContent>
        </StyledDialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{
              backgroundColor: snackbar.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
              color: snackbar.severity === 'success'
                ? theme.palette.getContrastText(theme.palette.success.main)
                : theme.palette.getContrastText(theme.palette.error.main)
            }}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Conversations;
